import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from "lodash"
import { handleInputBlur, handleInputChange, handleInputFocus, handleResultClick } from '../utils/SearchBar'
import { fuzzySearchProducts } from '../utils/fuzzySearch'
import { MainContext } from '../context/MainContext'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WhatsappIcon } from 'react-share'
import { buildWhatsappUrl } from '../assests/static'

const Search = () => {
  const [value, setValue] = useState("")
  const [results, setResults] = useState([])
  const [showResults, setShowResults] = useState(false)
  const navigate = useNavigate()

  // Fuzzy search runs entirely against the catalogue already loaded by
  // MainContext (no extra network call per keystroke). It's read via a ref
  // so the debounced handler below — created once — always sees the latest
  // list instead of whatever was loaded (possibly still empty) at mount.
  const { products } = useContext(MainContext)
  const productsRef = useRef(products)
  useEffect(() => {
    productsRef.current = products
  }, [products])

  const handleSearch = useMemo(
    () =>
      debounce((searchVal) => {
        setResults(fuzzySearchProducts(productsRef.current, searchVal))
        setShowResults(true)
      }, 300),
    []
  )

  // Trimmed, not the raw input: an empty/whitespace-only query already
  // clears results and hides the dropdown in handleInputChange, so this
  // only ever has a value when there's something real to show an enquiry
  // fallback for.
  const trimmedQuery = value.trim()

  return (
    <div className="relative w-full max-w-2xl md:mx-auto">
      <form onSubmit={(e) => e.preventDefault()} className="relative">
        <input
          type="search"
          placeholder="Search for products, brands and more..."
          aria-label="Search"
          value={value}
          onChange={(e) => handleInputChange(e, setValue, setResults, setShowResults, handleSearch)}
          onFocus={() => handleInputFocus(results, setShowResults)}
          onBlur={() => handleInputBlur(setShowResults)}
          className="w-full rounded-lg pl-11 pr-4 py-2.5 text-sm border border-gray-300 bg-gray-50 placeholder-gray-400 text-gray-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition duration-150"
        />
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400 text-base"
        />
      </form>

      {showResults && results.length > 0 && (
        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {results.map((result, index) => (
            <div
              key={index}
              onMouseDown={() => handleResultClick(result, setShowResults, navigate)}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer transition-colors duration-150"
            >
              {result.name}
            </div>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && trimmedQuery && (
        <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-lg p-4">
          <p className="text-sm text-gray-600">
            No products found for "
            <span
              className="font-semibold text-gray-800 align-bottom inline-block max-w-[200px] sm:max-w-[320px] truncate"
              title={trimmedQuery}
            >
              {trimmedQuery}
            </span>
            "
          </p>
          <p className="mt-1.5 text-sm text-gray-500">
            Looking for something specific? Send us an enquiry on WhatsApp and we'll help you find it.
          </p>
          {/* No onMouseDown-close here (unlike the product rows above): those
              navigate via JS inside the handler itself, but this is a real
              target="_blank" anchor whose navigation is the browser's native
              click action — closing the dropdown (and unmounting this link)
              on mousedown would remove it before that click ever fires. The
              input's existing onBlur close (delayed 200ms) is what dismisses
              this after the click goes through. */}
          <a
            href={buildWhatsappUrl(`Hi, I'm looking for ${trimmedQuery} on your website Dwarka Orthodontics. Do you have this product available?`)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"
          >
            <WhatsappIcon size={20} round />
            Ask on WhatsApp
          </a>
        </div>
      )}
    </div>
  )
}

export default Search
